<template>
    <div class="flex w-full xs:flex-col-reverse lg:flex-row xs:flex-wrap lg:flex-no-wrap xs:gap-0 xs:gap-y-3 lg:gap-3"
        ref="div"
        tabindex="0"
        @keydown="typeNumber"
        @keydown.enter="confirmInput"
        @keydown.backspace="clear"
        @keydown.esc="$emit('close')">

        <div class="my-card w-full flex flex-col justify-between">
            <PaymentList :bill="bill" slot="no-body"/>

            <div class="md:p-4">
                <vs-divider/>
                <PaymentTotals class="mt-6"
                    :selectedValue="selectedValue"
                    :totalValue="totalToPay"
                />
            </div>
        </div>

        <vx-card no-shadow card-border
            class="xs:w-full md:1/3 lg:3/5 xl:w-2/5">
            
            <PaymentBtnMethods v-model="type"
                :leftToPay="leftToPay"
                :selectedValue="selectedValue"
                @amountInput="setAmount"/>

            <div class="flex flex-col gap-y-4 items-center mt-2 py-4">

                <div class="relative width">
                    <div class="sm:hidden md:block eraser cursor-pointer" @click="clear">
                        <vue-mdi icon="backspace-outline" color="#CCC"/>
                    </div>
                    <currency-input
                        ref="currencyInput"
                        :distraction-free="{ hideCurrencySymbol : false }"
                        :disabled="payingAll"
                        useGrouping
                        :valueRange="valueRange"
                        currency="BRL"
                        class="vs-input--input danger h-16 text-right text-3xl font-bold"
                        v-model="value" />
                    <vs-button type="border" 
                        class="mt-2 mb-3 w-full xs:block md:hidden"
                        :disabled="payingAll || value == 0"
                        @click="addPayment">
                        Adicionar
                    </vs-button>
                </div>
                    
                <NumberInput ref="numberInput"
                    class="xs:hidden md:block"
                    :value="value"
                    :leftToPay="leftToPay"
                    :disabled="payingAll"
                    @comma="inputtedComma"
                    @clear="value = 0"
                    @inputted="inputtedValue"
                    @commit="addPayment"/>
            </div>

            <PaymentOptions class="md:mt-base"
                :totalValue="totalToPay"
                :leftToPay="leftToPay"
                :bill="bill"
                :canSendNfce="canSendNfce">
                <PaymentButton slot="after"
                    :imgSelected="require('@/assets/images/payment/confirmed_white.svg')"
                    selected :disabled="!canConfirm"
                    :hotKey="['ctrl','enter']"
                    class="red"
                    :class="{ 'red-disabled': !canConfirm }"
                    label="Confirmar"
                    @click="confirmPayment"
                    @shortkey="confirmPayment"
                />
            </PaymentOptions>
        </vx-card>
    </div>
</template>


<script>
    import PaymentBtnMethods from './PaymentBtnMethods'
    import PaymentOptions from './PaymentOptions.vue'
    import PaymentList from './PaymentList.vue'
    import NumberInput from './NumberInput.vue'
    import PaymentTotals from './PaymentTotals.vue'
    import PaymentButton from './PaymentButton.vue'
    import Template from '../table/templates/one/Template.vue'
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
    import { truncateNumber } from '@/assets/utils/misc.js'

    export default {
        components : {
            PaymentBtnMethods,
            PaymentOptions,
            PaymentList,
            NumberInput,
            PaymentTotals,
            PaymentButton,
            Template,
        },
        
        props : {
            bill : {
                type : Object,
                required : true,
            },
            usersOrdersToPay: Object,
            selectedValue: Number,
            calculatedTotalToPay: Number,
        },

        data () {
            return {
                value : 0,
                type: 'card_credit', // todo add config for default,
                lastInputWasComma: false,
            }
        },

        computed: {
            totalToPay () {
                return truncateNumber(this.calculatedTotalToPay + (this.taxesValue || 0)) - this.discount.absolute.value;
            },
            leftToPay () {
                // can be the remaining value of the bill or the selected value
                return truncateNumber(this.remaingingForSelectedValue ?? this.maxToPay ?? this.totalToPay);
            },
            remaingingForSelectedValue() {
                if (!this.selectedValue)
                    return;
                if (this.maxToPay < (this.selectedValue - this.totalTempValue))
                    return truncateNumber(this.selectedValue - (this.selectedValue - this.maxToPay));
                return truncateNumber(this.selectedValue - this.totalTempValue);
            },
            maxToPay () {
                return truncateNumber((this.totalToPay - this.totalPaidInPayments) ?? this.totalToPay);
            },
            valueRange () {
                return { min: 0, max: this.leftToPay };
            },
            payingAll () {
                return this.leftToPay <= 0;
            },
            canConfirm () {
                const baseValidation = this.tempPayments.length || !!this.value;
                if (this.selectedValue)
                    return baseValidation && !(this.leftToPay - this.value);
                return baseValidation;
            },
            isPaymentStandalone () {
                const paying = this.totalTempValue + this.value;
                return this.totalToPay !== paying
                    && this.selectedValue !== paying;
            },
            taxesValue () {
                // todo: this should not be fixed!
                const defaultConfig = this.bill.taxes.percentage.enabled;
                const userConfig = this.taxes.enabled;
                const areTheSameValue = defaultConfig ? userConfig : !userConfig
                const taxPercentageAbsoluteValue = this.bill.value / this.bill.taxes.percentage.value;
                return (areTheSameValue ? 0 : defaultConfig ? -1 : 1) * taxPercentageAbsoluteValue;
            },
            canSendNfce () {
                // todo add here check if is not a shared product being paid
                return !this.isPaymentStandalone;
            },
            ...mapState('payment', ['tempPayments', 'taxes', 'discount', 'nfce']),
            ...mapGetters('payment', ['totalPaidInPayments', 'totalTempValue']),
        },

        methods: {
            typeNumber(keyEvent) {
                const keyNumber = parseInt(keyEvent.key);
                if (keyNumber >= 0) {
                    this.$refs.numberInput.highlightNumber(keyNumber);
                    this.inputtedValue(keyNumber)
                } else if (keyEvent.key === ',' || keyEvent.key === '.') {
                    this.$refs.numberInput.highlightNumber(11);
                    this.inputtedComma();
                }
                else if(keyEvent.key.toUpperCase() === 'DELETE')
                    this.setAmount(0);
                else if(keyEvent.key === 't')
                    this.setAmount(this.leftToPay);
            },
            inputtedValue (v) {
                if (this.lastInputWasComma) {
                    this.lastInputWasComma = false;
                    this.value = +(`${this.value}.${v}`);
                } else
                    this.value = +(this.value + v.toString());
            },
            inputtedComma () {
                this.lastInputWasComma = true;
            },
            confirmInput () {
                this.$refs.numberInput.highlightNumber(10);
                this.addPayment();
            },
            clear () {
                const v = this.value.toString();
                this.value = v.length === 1 
                    ? 0
                    : +(v.split('').reverse().slice(1).reverse().reduce((prev, curr) => prev + curr));
            },
            addPayment () {
                const { value, type } = this;
                if (value == 0)
                    return;
                this.addTempPayment({
                    value,
                    type
                });
                this.value = 0;
            },
            setAmount (value) {
                this.value = value ?? this.leftToPay;
            },
            confirmPayment () {
                const { value, type, bill, isPaymentStandalone, usersOrdersToPay, maxToPay } = this;
                if (value)
                    this.addTempPayment({ value, type });
                const userOrders = isPaymentStandalone ? null : usersOrdersToPay;
                // do not wait
                this.commitPayments({ billId: bill.id, userOrders, table: bill.table });
                // if (this.nfce.enabled && this.canSendNfce)
                if (maxToPay - value <= 0)
                    this.$emit('payingAll');
                else
                    this.$emit('close');
            },
            truncate (value) {
                return Math.round(value * 100 + Number.EPSILON) / 100;
            },
            ...mapActions('payment', ['commitPayments']),
            ...mapMutations('payment', ['addTempPayment', 'resetPaymentState', 'setDiscountValue'])
        },

        mounted () {
            this.$nextTick(() => {
                this.$refs.div.focus();
            });
            this.value = this.selectedValue || this.leftToPay;
            this.setDiscountValue(this.bill.discountValue)
            if (!localStorage['sawTutorial']) {
                localStorage['sawTutorial'] = true;
                this.$confirm({
                    zIndex : 999999,
                    title: 'Bem vindo a nova tela de pagamento!',
                    content: 'Gostaria de ver um vídeo demonstrando as novas funcionalidades de pagamento?',
                    centered: true,
                    okText : 'Ver vídeo',
                    cancelText : 'Não',
                    okType: 'danger',
                    onOk: (close) => { this.$emit('tutorial'); close(); }
                })
            }
        },

        beforeDestroy () {
            this.resetPaymentState();
        }
    }
</script>
<style scoped>
    .my-card {
        border: 1px solid #e4e4e4;
        border-radius: .5rem;
    }
    
    @media (min-width: 500px) {
        .width {
            width: 80%;
            max-width: 330px;
        }    
    }
    
    .eraser {
        position: absolute;
        left: 16px;
        top: 16px;
        z-index: 999;
    }
    
    .red {
        color: white !important;
        background: #e04343;
    }
    .red:hover {
        background: #ff7b7b !important;
    }
    .red-disabled:hover {
        background: #e04343 !important;
    }
</style>